<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex flex-column me-3 mb-5 mb-lg-0">
          <h1 class="text-dark fw-bolder fs-3 my-1">{{ $t('landlord.menu.dashboard') }}</h1>
          <span class="text-muted fs-7">{{ $t('landlord.dashboard.overview_subtitle') }}</span>
        </div>
      </div>
    </div>
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-fluid">

        <!-- Chiffres clés de la plateforme -->
        <div class="row g-4 mb-4">
          <div v-for="kpi in kpis" :key="kpi.key" class="col-xl col-sm-6">
            <component :is="kpi.to ? 'router-link' : 'div'" :to="kpi.to" class="card card-flush h-100 kpi-card">
              <div class="card-body p-5">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <span class="text-gray-600 fw-bold fs-7 text-uppercase">{{ kpi.label }}</span>
                  <span class="kpi-icon"><i :class="kpi.icon"></i></span>
                </div>
                <div class="fs-2hx fw-bolder text-dark lh-1 mb-2">
                  <span v-if="loadingDashboard" class="spinner-border spinner-border-sm"></span>
                  <span v-else>{{ kpi.value }}</span>
                </div>
                <div class="text-gray-500 fs-7" :class="{ invisible: loadingDashboard }">{{ kpi.detail }}</div>
              </div>
            </component>
          </div>
        </div>

        <div class="row g-4 mb-4">
          <!-- À surveiller : ce qui demande une action du Landlord -->
          <div class="col-xl-8">
            <div class="card card-flush h-100">
              <div class="card-header border-0 pt-5">
                <h3 class="card-title fw-bolder fs-4">{{ $t('landlord.dashboard.to_watch') }}</h3>
              </div>
              <div class="card-body pt-2">
                <div class="row g-3 mb-5">
                  <div v-for="alert in alerts" :key="alert.key" class="col-md-3 col-6">
                    <component :is="alert.to ? 'router-link' : 'div'" :to="alert.to" class="alert-tile" :class="alert.value ? 'alert-' + alert.level : 'alert-quiet'">
                      <div class="fs-2 fw-bolder lh-1 mb-1">
                        <span v-if="loadingDashboard" class="spinner-border spinner-border-sm"></span>
                        <span v-else>{{ alert.value }}</span>
                      </div>
                      <div class="fs-8 fw-bold">{{ alert.label }}</div>
                    </component>
                  </div>
                </div>

                <div v-if="loadingDashboard" class="text-center py-6">
                  <span class="spinner-border text-primary"></span>
                </div>
                <div v-else-if="!toWatch.length" class="d-flex align-items-center text-gray-600 py-4">
                  <i class="bi bi-check2-circle fs-2 text-success me-3"></i>
                  {{ $t('landlord.dashboard.nothing_to_watch') }}
                </div>
                <div v-else class="table-responsive">
                  <table class="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3 mb-0">
                    <thead>
                      <tr class="fw-bolder text-muted fs-8 text-uppercase">
                        <th>{{ $t('landlord.dashboard.col_society') }}</th>
                        <th>{{ $t('landlord.dashboard.col_plan') }}</th>
                        <th>{{ $t('landlord.dashboard.col_expires') }}</th>
                        <th class="text-end">{{ $t('landlord.dashboard.col_subscription') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="society in toWatch" :key="society.id">
                        <td>
                          <router-link :to="societyLink(society)" class="text-dark fw-bold text-hover-primary">{{ society.name }}</router-link>
                        </td>
                        <td class="text-gray-700">{{ society.plan || '-' }}</td>
                        <td class="text-gray-700">
                          {{ formatDate(society.expires_at) }}
                          <span class="text-muted fs-8 ms-1">{{ delay(society) }}</span>
                        </td>
                        <td class="text-end">
                          <span class="badge" :class="stateBadge(society.state)">{{ $t('landlord.dashboard.state_' + society.state) }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div class="col-xl-4">
            <!-- Sociétés abonnées, par plan -->
            <div class="card card-flush mb-4">
              <div class="card-header border-0 pt-5">
                <h3 class="card-title fw-bolder fs-4">{{ $t('landlord.dashboard.by_plan') }}</h3>
                <div class="card-toolbar">
                  <router-link :to="{ name: 'landlord-plans' }" class="btn btn-sm btn-light-primary">{{ $t('landlord.dashboard.view_all') }}</router-link>
                </div>
              </div>
              <div class="card-body pt-2">
                <div v-if="loadingDashboard" class="text-center py-4">
                  <span class="spinner-border spinner-border-sm text-primary"></span>
                </div>
                <div v-else-if="!byPlan.length" class="text-gray-500 py-2">{{ $t('landlord.dashboard.no_subscription_yet') }}</div>
                <template v-else>
                  <div v-for="row in byPlan" :key="row.plan" class="mb-4">
                    <div class="d-flex justify-content-between fs-7 mb-1">
                      <span class="fw-bold text-gray-800">{{ row.plan }}</span>
                      <span class="fw-bolder text-dark">{{ row.count }}</span>
                    </div>
                    <div class="plan-bar"><div class="plan-bar-fill" :style="{ width: planShare(row) + '%' }"></div></div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Support -->
            <div class="card card-flush">
              <div class="card-header border-0 pt-5">
                <h3 class="card-title fw-bolder fs-4">{{ $t('landlord.dashboard.support') }}</h3>
                <div class="card-toolbar">
                  <router-link :to="{ name: 'landlord-tickets' }" class="btn btn-sm btn-light-primary">{{ $t('landlord.dashboard.view_all') }}</router-link>
                </div>
              </div>
              <div class="card-body pt-2">
                <div class="d-flex text-center">
                  <div v-for="ticket in ticketStats" :key="ticket.key" class="flex-fill">
                    <div class="fs-3 fw-bolder text-dark lh-1 mb-1">
                      <span v-if="loadingDashboard" class="spinner-border spinner-border-sm"></span>
                      <span v-else>{{ ticket.value }}</span>
                    </div>
                    <div class="text-gray-500 fs-8">{{ ticket.label }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sociétés récentes -->
        <div class="card card-flush">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title fw-bolder fs-4">{{ $t('landlord.dashboard.recent_societies') }}</h3>
            <div class="card-toolbar">
              <router-link :to="{ name: 'landlord-societies' }" class="btn btn-sm btn-light-primary">{{ $t('landlord.dashboard.view_all') }}</router-link>
            </div>
          </div>
          <div class="card-body pt-2">
            <div v-if="loadingDashboard" class="text-center py-10">
              <span class="spinner-border text-primary"></span>
            </div>
            <div v-else-if="!recentSocieties.length" class="text-center py-10 text-gray-500">
              {{ $t('landlord.dashboard.no_societies') }}
            </div>
            <div v-else class="table-responsive">
              <table class="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3 mb-0">
                <thead>
                  <tr class="fw-bolder text-muted fs-8 text-uppercase">
                    <th>{{ $t('landlord.dashboard.col_society') }}</th>
                    <th>{{ $t('landlord.dashboard.col_shops') }}</th>
                    <th>{{ $t('landlord.dashboard.col_plan') }}</th>
                    <th>{{ $t('landlord.dashboard.col_subscription') }}</th>
                    <th>{{ $t('landlord.dashboard.col_expires') }}</th>
                    <th class="text-end">{{ $t('landlord.dashboard.col_created') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="society in recentSocieties" :key="society.id">
                    <td>
                      <router-link :to="societyLink(society)" class="text-dark fw-bold text-hover-primary">{{ society.name }}</router-link>
                    </td>
                    <td class="text-gray-700">{{ society.shops_count }}</td>
                    <td class="text-gray-700">{{ society.plan || '-' }}</td>
                    <td>
                      <span class="badge" :class="stateBadge(society.state)">{{ $t('landlord.dashboard.state_' + society.state) }}</span>
                    </td>
                    <td class="text-gray-700">
                      <template v-if="society.state === 'none'">-</template>
                      <template v-else-if="!society.expires_at">{{ $t('landlord.dashboard.no_end_date') }}</template>
                      <template v-else>{{ formatDate(society.expires_at) }}</template>
                    </td>
                    <td class="text-end text-gray-500">{{ formatDate(society.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
    name: 'LandlordDashboardView',
    computed: {
        ...mapGetters('landlord', ['dashboard', 'loadingDashboard']),

        societies() { return this.dashboard?.societies || {} },
        shops() { return this.dashboard?.shops || {} },
        subscriptions() { return this.dashboard?.subscriptions || {} },
        users() { return this.dashboard?.users || {} },
        tickets() { return this.dashboard?.tickets || {} },
        trials() { return this.dashboard?.trials || {} },
        customerAccounts() { return this.dashboard?.customer_accounts || {} },
        toWatch() { return this.dashboard?.to_watch || [] },
        byPlan() { return this.subscriptions.by_plan || [] },
        recentSocieties() { return this.dashboard?.recent_societies || [] },
        soonDays() { return this.subscriptions.expiring_soon_days || 30 },

        kpis() {
            const t = (key, params) => this.$t('landlord.dashboard.' + key, params)
            const shopsDetail = t('shops_detail', { online: this.shops.online || 0, offline: this.shops.offline || 0 })
                + (this.shops.archived ? ' · ' + t('shops_archived', { count: this.shops.archived }) : '')

            return [
                {
                    key: 'societies', icon: 'bi bi-building', to: { name: 'landlord-societies' },
                    label: t('societies'), value: this.societies.total || 0,
                    detail: t('societies_new', { count: this.societies.new_this_month || 0 }),
                },
                {
                    key: 'shops', icon: 'bi bi-shop', to: { name: 'landlord-shops' },
                    label: t('shops'), value: this.shops.total || 0, detail: shopsDetail,
                },
                {
                    key: 'subscriptions', icon: 'bi bi-patch-check', to: { name: 'landlord-societies' },
                    label: t('subscriptions'), value: this.subscriptions.active || 0,
                    detail: t('subscriptions_expiring', { count: this.subscriptions.expiring || 0, days: this.soonDays }),
                },
                {
                    key: 'users', icon: 'bi bi-people', to: { name: 'landlord-societies' },
                    label: t('users'), value: this.users.total || 0,
                    detail: t('users_active', { count: this.users.active || 0 }),
                },
                {
                    // comptes clients Qomerxia : « plusieurs sociétés » = effet réseau
                    key: 'customer_accounts', icon: 'bi bi-person-badge', to: null,
                    label: t('customer_accounts'), value: this.customerAccounts.total || 0,
                    detail: t('customer_accounts_detail', {
                        multi: this.customerAccounts.multi_society || 0,
                        count: this.customerAccounts.new_this_month || 0,
                    }),
                },
            ]
        },

        alerts() {
            const t = (key, params) => this.$t('landlord.dashboard.' + key, params)

            return [
                // inscriptions depuis la landing (essai ou payante) à valider dans Boutiques
                { key: 'signups_pending', level: 'warning', value: this.dashboard?.signups_pending || 0, label: t('signups_pending'), to: { name: 'landlord-shops' } },
                { key: 'expired', level: 'danger', value: this.subscriptions.expired || 0, label: t('expired_subscriptions') },
                { key: 'expiring', level: 'warning', value: this.subscriptions.expiring || 0, label: t('expiring_subscriptions', { days: this.soonDays }) },
                { key: 'none', level: 'warning', value: this.societies.without_subscription || 0, label: t('without_subscription'), to: { name: 'landlord-societies' } },
                { key: 'tickets', level: 'warning', value: (this.tickets.new || 0) + (this.tickets.open || 0), label: t('tickets_to_handle'), to: { name: 'landlord-tickets' } },
                // essais gratuits : prospects à rappeler, essais finis sans souscription, essais convertis
                { key: 'trials', level: 'primary', value: this.trials.active || 0, label: t('trials_active') },
                { key: 'trials_ended', level: 'danger', value: this.trials.ended || 0, label: t('trials_ended') },
                { key: 'trials_converted', level: 'success', value: this.trials.converted || 0, label: t('trials_converted') },
            ]
        },

        ticketStats() {
            return [
                { key: 'new', value: this.tickets.new || 0, label: this.$t('landlord.tickets.status_new') },
                { key: 'open', value: this.tickets.open || 0, label: this.$t('landlord.tickets.status_open') },
                { key: 'closed', value: this.tickets.closed || 0, label: this.$t('landlord.tickets.status_closed') },
            ]
        },
    },
    mounted() {
        this.fetchDashboard()
    },
    methods: {
        ...mapActions('landlord', ['fetchDashboard']),

        societyLink(society) {
            return { name: 'landlord-society-detail', params: { id: society.id } }
        },

        // part d'un plan parmi les sociétés abonnées
        planShare(row) {
            const total = this.byPlan.reduce((sum, plan) => sum + plan.count, 0)
            return total ? Math.round((row.count / total) * 100) : 0
        },

        stateBadge(state) {
            return {
                active: 'badge-light-success',
                expiring: 'badge-light-warning',
                expired: 'badge-light-danger',
                trial: 'badge-light-primary',
                trial_ended: 'badge-light-danger',
                none: 'badge-light',
            }[state] || 'badge-light'
        },

        // « dans 12 j » / « depuis 3 j » à côté de la date d'échéance
        delay(society) {
            if (society.days_left === null || society.days_left === undefined) return ''
            if (society.days_left === 0 && society.state !== 'expired') return this.$t('landlord.dashboard.today')
            return society.state === 'expired'
                ? this.$t('landlord.dashboard.days_late', { count: Math.abs(society.days_left) })
                : this.$t('landlord.dashboard.days_left', { count: society.days_left })
        },

        formatDate(dateStr) {
            if (!dateStr) return '-'
            const d = new Date(String(dateStr).replace(' ', 'T'))
            return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        }
    }
}
</script>

<style scoped>
.kpi-card {
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.kpi-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

/* Icônes : une seule couleur (celle de la plateforme), comme le reste du back-office */
.kpi-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  font-size: 1.1rem;
  background: rgba(var(--kt-primary-rgb), 0.12);
  color: var(--kt-primary);
}
.kpi-icon i {
  color: inherit;
}

/* Tuiles « À surveiller » : colorées seulement quand il y a quelque chose à traiter */
.alert-tile {
  display: block;
  height: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  text-decoration: none;
}
.alert-quiet {
  background: #f5f8fa;
  color: #7e8299;
}
.alert-warning {
  background: #fff8dd;
  color: #9a6b00;
}
.alert-danger {
  background: #fff5f8;
  color: #d9214e;
}
.alert-primary {
  background: #f1faff;
  color: #0b3d87;
}
.alert-success {
  background: #e8fff3;
  color: #47be7d;
}

.plan-bar {
  height: 6px;
  border-radius: 3px;
  background: #eff2f5;
  overflow: hidden;
}
.plan-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: var(--kt-primary);
}
</style>
